import { useParams, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getComicBySlug, getComicsByCategory } from '@/mocks/comics';

export default function ComicDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const comic = useMemo(() => (slug ? getComicBySlug(slug) : undefined), [slug]);
  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return getComicsByCategory(comic.category).filter(c => c.id !== comic.id).slice(0, 4);
  }, [comic]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-semibold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">该漫画不存在或已被移除</p>
            <button
              onClick={() => navigate('/')}
              className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${comic.title} - 非麻瓜漫画 | 免费下拉式阅读`}
        description={`${comic.description} 非麻瓜漫画提供${comic.title}免费下拉式全集在线阅读，高清画质，流畅体验。`}
        keywords={`${comic.title},${comic.categoryName},非麻瓜漫画,免费漫画,下拉式漫画,在线漫画`}
        canonicalUrl={`https://anhuicloud.cn/comic/${comic.slug}`}
        ogTitle={`${comic.title} - 非麻瓜漫画`}
        ogDescription={comic.description}
        ogImage={comic.cover}
        ogType="article"
        article={{
          publishedTime: comic.createdAt,
          modifiedTime: comic.updatedAt,
          author: comic.author,
          tags: comic.tags,
        }}
      />
      <SchemaOrg data={[{
        '@type': 'ComicSeries',
        name: comic.title,
        description: comic.description,
        url: `https://anhuicloud.cn/comic/${comic.slug}`,
        image: comic.cover,
        author: {
          '@type': 'Person',
          name: comic.author,
        },
        dateCreated: comic.createdAt,
        dateModified: comic.updatedAt,
        inLanguage: 'zh-CN',
      }, {
        '@type': 'Article',
        headline: `${comic.title} - 非麻瓜漫画`,
        description: comic.description,
        image: comic.cover,
        author: { '@type': 'Person', name: comic.author },
        datePublished: comic.createdAt,
        dateModified: comic.updatedAt,
      }]} />

      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { name: '首页', href: '/' },
                { name: comic.categoryName, href: `/category/${comic.category}` },
                { name: comic.title },
              ]}
            />
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            <div className="w-full lg:w-72 shrink-0">
              <div className="sticky top-24">
                <div className="rounded-xl overflow-hidden mb-5">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 非麻瓜漫画`}
                    className="w-full"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => { if (comic.chapters[0]) navigate(`/comic/${comic.slug}/read/${comic.chapters[0].number}`); }}
                    className="flex-1 px-5 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <i className="ri-play-circle-line text-lg"></i>开始阅读
                  </button>
                  <button className="w-12 h-12 flex items-center justify-center bg-background-100 text-foreground-500 rounded-lg hover:bg-accent-100 hover:text-accent-500 transition-colors cursor-pointer">
                    <i className="ri-heart-line text-lg"></i>
                  </button>
                  <button className="w-12 h-12 flex items-center justify-center bg-background-100 text-foreground-500 rounded-lg hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer">
                    <i className="ri-bookmark-line text-lg"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  comic.status === '已完结'
                    ? 'bg-secondary-100 text-secondary-700'
                    : 'bg-accent-100 text-accent-700'
                }`}>
                  {comic.status}
                </span>
                <span className="text-sm text-foreground-500">{comic.categoryName}</span>
                <span className="text-sm text-foreground-400">·</span>
                <span className="flex items-center gap-1 text-sm text-foreground-500">
                  <i className="ri-star-fill text-primary-500"></i>
                  <strong className="text-foreground-700">{comic.rating}</strong>
                </span>
                <span className="text-sm text-foreground-400">·</span>
                <span className="text-sm text-foreground-500">
                  <i className="ri-eye-line mr-1"></i>{(comic.views / 10000).toFixed(0)}万次阅读
                </span>
                <span className="text-sm text-foreground-400">·</span>
                <span className="text-sm text-foreground-500">
                  <i className="ri-heart-line mr-1"></i>{(comic.favorites / 1000).toFixed(0)}k收藏
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 p-5 bg-background-100 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-background-50 rounded-lg">
                    <i className="ri-user-line text-foreground-500"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">作者</p>
                    <p className="text-sm font-medium text-foreground-950">{comic.author}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-background-50 rounded-lg">
                    <i className="ri-paint-brush-line text-foreground-500"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">画师</p>
                    <p className="text-sm font-medium text-foreground-950">{comic.artist}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-background-50 rounded-lg">
                    <i className="ri-file-list-3-line text-foreground-500"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">总章节</p>
                    <p className="text-sm font-medium text-foreground-950">{comic.chapters.length}话</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-background-50 rounded-lg">
                    <i className="ri-calendar-line text-foreground-500"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">最近更新</p>
                    <p className="text-sm font-medium text-foreground-950">{comic.updatedAt}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-secondary-50 text-secondary-700 rounded-full text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mb-8">
                <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-3">作品简介</h2>
                <p className="text-foreground-600 leading-relaxed text-sm md:text-base">
                  {comic.longDescription}
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading font-semibold text-lg text-foreground-950">章节列表</h2>
                  <div className="flex items-center gap-2">
                    <button
                      className="px-3 py-1.5 bg-background-100 text-foreground-600 rounded-md text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      正序 <i className="ri-arrow-down-line ml-1"></i>
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-[500px] overflow-y-auto pr-1">
                  {comic.chapters.map((chapter) => (
                    <a
                      key={chapter.id}
                      href={`/comic/${comic.slug}/read/${chapter.number}`}
                      onClick={(e) => { e.preventDefault(); navigate(`/comic/${comic.slug}/read/${chapter.number}`); }}
                      className="flex items-center justify-between px-4 py-3 bg-background-100 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer group"
                    >
                      <span className="text-sm text-foreground-700 group-hover:text-primary-600 truncate">
                        {chapter.title}
                      </span>
                      <span className="text-xs text-foreground-400 shrink-0 ml-3">{chapter.updatedAt}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {relatedComics.length > 0 && (
            <div className="mt-14 pt-10 border-t border-background-200/70">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-heading font-bold text-foreground-950">相关推荐</h2>
                <a
                  href={`/category/${comic.category}`}
                  onClick={(e) => { e.preventDefault(); navigate(`/category/${comic.category}`); }}
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看更多 <i className="ri-arrow-right-line ml-1"></i>
                </a>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                {relatedComics.map((rc) => (
                  <article
                    key={rc.id}
                    className="group cursor-pointer"
                    onClick={() => navigate(`/comic/${rc.slug}`)}
                  >
                    <div className="relative overflow-hidden rounded-xl mb-3 aspect-[3/4]">
                      <img
                        src={rc.cover}
                        alt={rc.title}
                        title={`${rc.title} - 非麻瓜漫画`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-950 text-sm group-hover:text-primary-500 transition-colors line-clamp-1">
                      {rc.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{rc.author}</p>
                  </article>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}